/**
 * credit-widgets.tsx — Mảnh giao diện lượt AI dùng chung: chip số dư ở header trang AI, banner hết lượt, dải ví ở tab
 * Sử dụng, badge nguồn. Số dư hiện "N lượt AI" (walletHeadline) — không ≈ token / đ. API ví chưa có (null) → không hiện gì.
 */
import type { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Coins, History, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { currentLocale } from '@/lib/i18n'
import { useCanBuyAiCredits } from '@/hooks/use-ai-credits'
import {
  amountText, deriveWalletStatus, tokenRateOf, walletHeadline, WALLET_STATUS_META,
  type AiCredits, type BadgeTone,
} from '@/lib/ai-credits'

export function ToneBadge({ tone, children, className }: { tone: BadgeTone; children: ReactNode; className?: string }) {
  return <Badge variant={tone} className={cn('whitespace-nowrap', className)}>{children}</Badge>
}

/** Chip "30.400 lượt AI [Mua thêm]" cạnh tiêu đề trang AI. */
export function AiCreditChip({ credits, onBuy }: { credits: AiCredits; onBuy: () => void }) {
  const { t } = useTranslation('aiOps')
  const status = deriveWalletStatus(credits)
  const head = walletHeadline(credits)
  const canBuy = useCanBuyAiCredits()
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-full border bg-card py-1 pl-3 text-sm shadow-sm',
        canBuy ? 'pr-1' : 'pr-3',
        status === 'empty' || status === 'blocked' ? 'border-destructive/40' : status === 'low' ? 'border-warning/40' : '',
      )}
    >
      <Coins className="h-4 w-4 text-primary" aria-hidden />
      <span title={head.sub ?? undefined}>
        <b className="tabular-nums">{head.main}</b>
      </span>
      {canBuy && (
        <Button size="sm" className="h-7 rounded-full px-3" onClick={onBuy}>
          <Plus className="h-3.5 w-3.5" /> {t('creditWidgets.buyMore')}
        </Button>
      )}
    </div>
  )
}

/** Banner khi ví hết credit / bị khoá. */
export function AiCreditBanner({ credits, onBuy }: { credits: AiCredits; onBuy: () => void }) {
  const { t } = useTranslation('aiOps')
  const status = deriveWalletStatus(credits)
  const canBuy = useCanBuyAiCredits()
  if (status !== 'empty' && status !== 'blocked') return null
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
      <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
      <p className="flex-1">
        {status === 'empty' ? (
          <Trans t={t} i18nKey="creditWidgets.bannerEmpty" components={{ b: <b /> }} />
        ) : (
          <Trans t={t} i18nKey="creditWidgets.bannerBlocked" components={{ b: <b /> }} />
        )}
      </p>
      {status === 'empty' && (canBuy
        ? <Button size="sm" onClick={onBuy}>{t('creditWidgets.buyCredits')}</Button>
        : <span className="text-xs text-muted-foreground">{t('creditWidgets.askAdmin')}</span>)}
    </div>
  )
}

/** Dải ví ở đầu tab Sử dụng. "Lịch sử": `onHistory` (cuộn tại chỗ) hoặc `historyHref` (màn khác → mở tab mới). */
export function WalletStrip({
  credits, planName, onBuy, onHistory, historyHref,
}: {
  credits: AiCredits
  planName?: string | null
  onBuy: () => void
  onHistory?: () => void
  historyHref?: string
}) {
  const { t } = useTranslation('aiOps')
  const status = deriveWalletStatus(credits)
  const meta = WALLET_STATUS_META[status]
  const head = walletHeadline(credits)
  const canBuy = useCanBuyAiCredits()
  const rate = tokenRateOf(credits)
  const parts = [planName ? t('creditWidgets.planName', { name: planName }) : null].filter(Boolean)
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{t('creditWidgets.orgCredits')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-2xl font-semibold tabular-nums">{head.main}</span>
            <ToneBadge tone={meta.tone}>{meta.label}</ToneBadge>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('creditWidgets.grantBalance', { amount: amountText(credits.grantBalance, rate) })}
            {credits.grantPeriodEnd ? t('creditWidgets.grantExpires', { date: new Date(credits.grantPeriodEnd).toLocaleDateString(currentLocale()) }) : ''}
            {' · '}{t('creditWidgets.paidBalance', { amount: amountText(credits.paidBalance, rate) })}
          </p>
          {parts.length > 0 && <p className="text-xs text-muted-foreground">{parts.join(' · ')}</p>}
        </div>
        <div className="flex gap-2">
          {historyHref
            ? <OpenInNewTab to={historyHref} variant="outline" title={t('creditWidgets.historyTitle')}><History className="h-4 w-4" /> {t('creditWidgets.history')}</OpenInNewTab>
            : onHistory && <Button variant="outline" onClick={onHistory}><History className="h-4 w-4" /> {t('creditWidgets.history')}</Button>}
          {canBuy && <Button onClick={onBuy}><Plus className="h-4 w-4" /> {t('creditWidgets.buyCredits')}</Button>}
        </div>
      </CardContent>
    </Card>
  )
}
